import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import {
    fetchConfigTemplatePlace,
    fetchConfigTemplateStates,
    fetchConfigTemplateTags,
    setPlaceFilters,
    upsertPlaceRow,
    useConfigTemplatePlace,
    useConfigTemplateStates,
    useConfigTemplateTags,
    type ConfigTemplatePlaceFilters,
} from '../../../store/slices/configTemplatesListSlice';
import { updateConfigTemplateStatus } from '../../../services/configTemplateService';
import type {
    ConfigTemplateActiveStatus,
    ConfigTemplateLevel,
    ConfigTemplateLicenceType,
} from '../../../types/configTemplate.types';
import { editorPath } from '../configTemplateCopy';

const SEARCH_DEBOUNCE_MS = 300;

export function useTemplatesList(level: ConfigTemplateLevel) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const place = useConfigTemplatePlace(level);
    const states = useConfigTemplateStates();
    const tags = useConfigTemplateTags(level);
    const [searchInput, setSearchInput] = useState(place.filters.search);
    const [rowBusy, setRowBusy] = useState<Record<string, 'status' | 'duplicate' | undefined>>({});
    const [rowError, setRowError] = useState<Record<string, boolean>>({});
    const searchTimer = useRef<number | null>(null);

    useEffect(() => {
        setSearchInput(place.filters.search);
    }, [place.filters.search]);

    useEffect(() => {
        if (place.status === 'idle') {
            void dispatch(fetchConfigTemplatePlace({ level }));
        }
    }, [dispatch, level, place.status]);

    useEffect(() => {
        if (states.status === 'idle') {
            void dispatch(fetchConfigTemplateStates());
        }
    }, [dispatch, states.status]);

    useEffect(() => {
        if (tags.status === 'idle') {
            void dispatch(fetchConfigTemplateTags({ level }));
        }
    }, [dispatch, level, tags.status]);

    const refresh = useCallback(() => {
        void dispatch(fetchConfigTemplatePlace({ level }));
    }, [dispatch, level]);

    const applyFilters = useCallback(
        (next: Partial<ConfigTemplatePlaceFilters>) => {
            dispatch(setPlaceFilters({ level, filters: next }));
            void dispatch(fetchConfigTemplatePlace({ level }));
        },
        [dispatch, level]
    );

    const onSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            if (searchTimer.current != null) window.clearTimeout(searchTimer.current);
            searchTimer.current = window.setTimeout(() => {
                dispatch(setPlaceFilters({ level, filters: { search: value } }));
                void dispatch(fetchConfigTemplatePlace({ level }));
            }, SEARCH_DEBOUNCE_MS);
        },
        [dispatch, level]
    );

    const clearFilters = useCallback(() => {
        setSearchInput('');
        dispatch(
            setPlaceFilters({
                level,
                filters: {
                    search: '',
                    states: [],
                    tags: [],
                    licenceTypes: [],
                    activeStatus: [],
                },
            })
        );
        void dispatch(fetchConfigTemplatePlace({ level }));
    }, [dispatch, level]);

    const loadMore = useCallback(() => {
        if (place.loadingMore) return;
        void dispatch(fetchConfigTemplatePlace({ level, append: true }));
    }, [dispatch, level, place.loadingMore]);

    const setStateFilter = useCallback((states: string[]) => applyFilters({ states }), [applyFilters]);
    const setTagFilter = useCallback((tags: string[]) => applyFilters({ tags }), [applyFilters]);
    const toggleActiveFilter = useCallback(
        (status: ConfigTemplateActiveStatus) => {
            const current = place.filters.activeStatus;
            const next = current.includes(status)
                ? current.filter((item) => item !== status)
                : [...current, status];
            applyFilters({ activeStatus: next });
        },
        [applyFilters, place.filters.activeStatus]
    );
    const setLicenceFilter = useCallback(
        (licenceTypes: ConfigTemplateLicenceType[]) => applyFilters({ licenceTypes }),
        [applyFilters]
    );
    const toggleNameSort = useCallback(() => {
        applyFilters({ sort: place.filters.sort === 'name' ? '-name' : 'name' });
    }, [applyFilters, place.filters.sort]);

    const openEditor = useCallback(
        (id: string) => {
            navigate(editorPath(level, id));
        },
        [level, navigate]
    );

    const openNew = useCallback(() => {
        navigate(editorPath(level));
    }, [level, navigate]);

    const duplicate = useCallback(
        (id: string) => {
            navigate(`${editorPath(level)}?duplicateFrom=${id}`);
        },
        [level, navigate]
    );

    const toggleStatus = useCallback(
        async (id: string, current: ConfigTemplateActiveStatus) => {
            const next = current === 'Active' ? 'Inactive' : 'Active';
            setRowBusy((prev) => ({ ...prev, [id]: 'status' }));
            setRowError((prev) => ({ ...prev, [id]: false }));
            try {
                const updated = await updateConfigTemplateStatus({ id, activeStatus: next });
                dispatch(upsertPlaceRow({ level, row: updated }));
                setRowBusy((prev) => ({ ...prev, [id]: undefined }));
            } catch {
                setRowBusy((prev) => ({ ...prev, [id]: undefined }));
                setRowError((prev) => ({ ...prev, [id]: true }));
            }
        },
        [dispatch, level]
    );

    const hasActiveFilters = Boolean(
        place.filters.search.trim() ||
            place.filters.states.length ||
            place.filters.tags.length ||
            place.filters.licenceTypes.length ||
            place.filters.activeStatus.length
    );

    return {
        place,
        states,
        tags,
        searchInput,
        onSearchChange,
        clearFilters,
        setStateFilter,
        setTagFilter,
        toggleActiveFilter,
        setLicenceFilter,
        toggleNameSort,
        loadMore,
        refresh,
        retryStates: () => {
            void dispatch(fetchConfigTemplateStates());
        },
        retryTags: () => {
            void dispatch(fetchConfigTemplateTags({ level }));
        },
        openEditor,
        openNew,
        duplicate,
        toggleStatus,
        rowBusy,
        rowError,
        hasActiveFilters,
        waitingSearch: searchInput !== place.filters.search,
    };
}
